import { ArpSegment } from "models/UserArpsModel";

import { CUBIC_FEET_TO_MCF } from "./arpsConstants";
import { getDurationInDays } from "./dates";
import { arpsWasm } from "./wasmLoader";

export interface RestrictedEURResult {
  restrictedEUR: number;
  qfAtCutoff: number;
}

/**
 * Calculate Restricted EUR for a SINGLE SEGMENT product
 *
 * This matches WASM's `restrict_secondary_products_end_dates` approach:
 * - WASM generates monthly forecast data points (typically on day 1 of each month)
 * - WASM filters out months where point.day > primary.day
 * - Monthly points have cumulative values representing production through END of previous month
 * - For a primary end date of Nov 22, WASM keeps the Nov 1 point
 * - The cumulative at Nov 1 represents production through Oct 31
 * - The rate at Nov 1 is the flow rate on Nov 1
 *
 * We calculate:
 * 1. EUR to end of month BEFORE primary end month (e.g., Oct 31 for Nov 22 primary end)
 * 2. qfAtCutoff as rate at start of cutoff month
 */
export function calculateRestrictedEURSingleSegment(
  segment: ArpSegment,
  primaryProductEndDate: Date
): RestrictedEURResult {
  const date = primaryProductEndDate.getUTCDate();

  // If the primary product end date is after the 15th of the month, use the current month
  // Otherwise, use the previous month for the cutoff calculation
  const monthOffset = date > 15 ? 1 : 0;

  // Calculate to end of previous month for gas (e.g., Oct 31 for Nov 22)
  // or end of current month for oil (e.g., Nov 30 for Nov 22)
  const cutoffDate = new Date(
    Date.UTC(
      primaryProductEndDate.getUTCFullYear(),
      primaryProductEndDate.getUTCMonth() + monthOffset,
      0, // Day 0 = last day of previous month
      23,
      59,
      59,
      999
    )
  );

  // Start of cutoff month (matches WASM's monthly point)
  const cutoffMonthStart = new Date(
    Date.UTC(
      primaryProductEndDate.getUTCFullYear(),
      primaryProductEndDate.getUTCMonth() + monthOffset,
      1, // First day of cutoff month
      0,
      0,
      0,
      0
    )
  );

  const segmentStartTimestamp = Math.round(new Date(segment.startDate).getTime() / 1000);
  const cutoffTimestamp = Math.round(cutoffDate.getTime() / 1000);

  // Calculate cumulative volume to end of previous month
  const restrictedEUR =
    arpsWasm.calculateSegmentCumulativeVolumeOverDays(
      segment,
      BigInt(segmentStartTimestamp),
      BigInt(cutoffTimestamp)
    ) * CUBIC_FEET_TO_MCF;

  // Calculate flow rate at START of cutoff month (matching WASM's monthly point)
  const timeElapsedToMonthStart = getDurationInDays(
    segment.startDate,
    cutoffMonthStart.toISOString()
  );

  const qfAtCutoff = arpsWasm.getRateAtDay(
    segment.qi,
    segment.di,
    segment.b,
    timeElapsedToMonthStart
  );

  return {
    restrictedEUR,
    qfAtCutoff
  };
}
